<template>
  <div class="finance-page">
    <PageHeader icon="fas fa-chart-pie">
      <template #title>Finance Center Klinik</template>
      <template #subtitle
        >Kas, piutang, utang, akuntansi, aset, anggaran, dan laporan keuangan
        dalam satu ruang kerja</template
      >
      <template #actions
        ><button class="btn-main" @click="loadAll">
          <i class="fas fa-sync-alt"></i> Perbarui Data
        </button></template
      >
    </PageHeader>

    <WorkspaceTabs
      :value="activeTab"
      :items="tabs"
      aria-label="Navigasi Finance Center"
      @change="selectTab"
    />

    <template v-if="activeTab === 'dashboard'">
      <div class="metric-grid">
        <article v-for="m in metrics" :key="m.label">
          <span :class="m.tone"><i :class="m.icon"></i></span>
          <div>
            <small>{{ m.label }}</small
            ><strong>{{ money(m.value) }}</strong>
          </div>
        </article>
      </div>
      <div class="business-unit-grid" v-if="(dashboard.business_units || []).length">
        <article v-for="unit in dashboard.business_units" :key="unit.code" class="panel">
          <div><small>UNIT USAHA</small><h3>{{ unit.name }}</h3></div>
          <div class="summary-row"><span>Pendapatan</span><b>{{ money(unit.revenue) }}</b></div>
          <div class="summary-row"><span>Beban + HPP</span><b>{{ money(unit.expense) }}</b></div>
          <div class="summary-row total"><span>Laba bersih</span><b>{{ money(unit.net_profit) }}</b></div>
          <div class="unit-margin">Margin {{ Number(unit.margin_percent || 0).toFixed(2) }}%</div>
        </article>
      </div>
      <div class="two-grid">
        <section class="panel">
          <PanelTitle
            eyebrow="KESEHATAN KEUANGAN"
            title="Ringkasan periode berjalan"
            icon="fas fa-heartbeat"
          />
          <div class="summary-row">
            <span>Pendapatan</span><b>{{ money(dashboard.revenue) }}</b>
          </div>
          <div class="summary-row">
            <span>Beban</span><b>{{ money(dashboard.expenses) }}</b>
          </div>
          <div class="summary-row total">
            <span>Laba bersih</span><b>{{ money(dashboard.net_profit) }}</b>
          </div>
          <div
            :class="[
              'balance-alert',
              Math.abs(dashboard.balance_check || 0) < 1 ? 'ok' : 'warn',
            ]"
          >
            <i
              :class="
                Math.abs(dashboard.balance_check || 0) < 1
                  ? 'fas fa-check-circle'
                  : 'fas fa-exclamation-triangle'
              "
            ></i
            >{{
              Math.abs(dashboard.balance_check || 0) < 1
                ? "Ledger seimbang"
                : "Selisih neraca " + money(dashboard.balance_check)
            }}
          </div>
        </section>
        <section class="panel">
          <PanelTitle
            eyebrow="AGING"
            title="Umur piutang"
            icon="fas fa-hourglass-half"
          />
          <div class="aging-bars">
            <div v-for="a in agingRows" :key="a.label">
              <span>{{ a.label }}</span>
              <div><i :style="{ width: a.width + '%' }"></i></div>
              <b>{{ money(a.value) }}</b>
            </div>
          </div>
        </section>
      </div>
      <div class="control-grid">
        <article
          :class="
            Math.abs(Number(dashboard.ar_subledger_difference || 0)) < 0.01
              ? 'ok'
              : 'warn'
          "
        >
          <i class="fas fa-balance-scale"></i>
          <div>
            <small>Kontrol Piutang</small
            ><b>{{ money(dashboard.ar_control_balance) }}</b
            ><span
              >Selisih subledger:
              {{ money(dashboard.ar_subledger_difference) }}</span
            >
          </div>
        </article>
        <article
          :class="
            Math.abs(Number(dashboard.ap_subledger_difference || 0)) < 0.01
              ? 'ok'
              : 'warn'
          "
        >
          <i class="fas fa-file-contract"></i>
          <div>
            <small>Kontrol Utang</small
            ><b>{{ money(dashboard.ap_control_balance) }}</b
            ><span
              >Selisih subledger:
              {{ money(dashboard.ap_subledger_difference) }}</span
            >
          </div>
        </article>
        <article
          :class="
            Number(dashboard.medicines_without_cost || 0) === 0 ? 'ok' : 'warn'
          "
        >
          <i class="fas fa-pills"></i>
          <div>
            <small>Kelengkapan Harga Pokok</small
            ><b>{{ dashboard.medicines_without_cost || 0 }} item belum diisi</b
            ><span>Wajib lengkap untuk laporan laba apotek.</span>
          </div>
        </article>
        <article
          :class="
            Math.abs(Number(dashboard.inventory_difference || 0)) < 0.01
              ? 'ok'
              : 'warn'
          "
        >
          <i class="fas fa-boxes"></i>
          <div>
            <small>Kontrol Persediaan</small
            ><b>{{ money(dashboard.inventory_control_balance) }}</b
            ><span>Nilai stok {{ money(dashboard.inventory_value) }} · Selisih {{ money(dashboard.inventory_difference) }}</span>
          </div>
        </article>
        <article :class="Math.abs(Number(dashboard.commission_difference || 0)) < 0.01 ? 'ok' : 'warn'">
          <i class="fas fa-user-tie"></i>
          <div>
            <small>Kontrol Utang Komisi</small><b>{{ money(dashboard.commission_payable) }}</b>
            <span>Subledger {{ money(dashboard.commission_subledger) }} · Selisih {{ money(dashboard.commission_difference) }}</span>
          </div>
        </article>
        <article :class="Number((dashboard.data_quality || {}).unbalanced_journals || 0) === 0 && Number((dashboard.data_quality || {}).unassigned_profit_loss_lines || 0) === 0 ? 'ok' : 'warn'">
          <i class="fas fa-shield-alt"></i>
          <div>
            <small>Kualitas Data Keuangan</small><b>{{ (dashboard.data_quality || {}).unbalanced_journals || 0 }} jurnal tidak seimbang</b>
            <span>{{ (dashboard.data_quality || {}).unassigned_profit_loss_lines || 0 }} baris laba-rugi belum punya unit/cost center</span>
          </div>
        </article>
      </div>
    </template>

    <template v-else-if="activeTab === 'journals'">
      <SectionHeader
        title="Jurnal & Buku Besar"
        subtitle="Setiap jurnal posted wajib seimbang dan tidak dapat dihapus"
        button="Buat Jurnal"
        @action="showJournalForm = !showJournalForm"
      />
      <div class="context-actions">
        <span
          ><i class="fas fa-lock"></i> Jurnal posted tidak dapat dihapus</span
        >
        <button class="btn-outline" @click="openOperation('close-period')">
          <i class="fas fa-calendar-check"></i> Tutup Periode
        </button>
      </div>
      <section v-if="showJournalForm" class="panel form-panel">
        <div class="form-grid">
          <label
            >Tanggal<input
              type="date"
              v-model="journalForm.journal_date" /></label
          ><label class="wide"
            >Keterangan<input
              v-model="journalForm.description"
              placeholder="Keterangan transaksi"
          /></label>
        </div>
        <div v-for="(l, i) in journalForm.lines" :key="i" class="journal-line">
          <select v-model="l.finance_account_id">
            <option value="">Pilih akun</option>
            <option v-for="a in masters.accounts" :key="a.id" :value="a.id">
              {{ a.code }} · {{ a.name }}
            </option></select
          ><select v-model="l.finance_cost_center_id">
            <option :value="null">Pilih cost center</option>
            <option v-for="c in masters.cost_centers" :key="c.id" :value="c.id">
              {{ c.name }}
            </option></select
          ><input
            type="number"
            v-model.number="l.debit"
            placeholder="Debit"
          /><input
            type="number"
            v-model.number="l.credit"
            placeholder="Kredit"
          /><button @click="journalForm.lines.splice(i, 1)">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="form-actions">
          <button class="btn-link" @click="journalForm.lines.push(blankLine())">
            <i class="fas fa-plus"></i> Tambah baris</button
          ><span
            >Debit {{ money(journalTotals.debit) }} · Kredit
            {{ money(journalTotals.credit) }}</span
          ><button
            class="btn-main"
            :disabled="
              journalTotals.debit !== journalTotals.credit ||
              journalTotals.debit <= 0
            "
            @click="saveJournal"
          >
            Posting Jurnal
          </button>
        </div>
      </section>
      <DataTable
        searchable
        :headers="[
          'Tanggal',
          'Nomor',
          'Keterangan',
          'Sumber',
          'Status',
          'Aksi',
        ]"
        :rows="journals"
        ><template #row="{ row }"
          ><td>{{ date(row.journal_date) }}</td>
          <td>
            <b>{{ row.journal_number }}</b>
          </td>
          <td>{{ row.description }}</td>
          <td>{{ label(row.source_type) }}</td>
          <td><StatusBadge :status="row.status" /></td>
          <td class="row-actions">
            <button title="Lihat jurnal" @click="showJournal(row)">
              <i class="fas fa-eye"></i></button
            ><button
              v-if="row.status === 'posted'"
              class="danger"
              title="Reverse jurnal"
              @click="openOperation('reverse', row)"
            >
              <i class="fas fa-undo-alt"></i>
            </button></td></template
      ></DataTable>
      <div class="accounting-columns">
        <section class="panel accounting-panel">
          <PanelTitle
            eyebrow="MASTER AKUN"
            title="Chart of Accounts"
            icon="fas fa-sitemap"
          />
          <div class="coa-toolbar">
            <span><i class="fas fa-lock"></i> Akun integrasi dikunci untuk menjaga jurnal otomatis.</span>
            <button class="btn-main" @click="openAccountForm()"><i class="fas fa-plus"></i> Tambah Akun</button>
          </div>
          <DataTable
            searchable
            :headers="[
              'Kode',
              'Nama Akun',
              'Tipe',
              'Saldo Normal',
              'Saldo Saat Ini',
              'Status',
              'Aksi',
            ]"
            :rows="masters.accounts"
            ><template #row="{ row }"
              ><td>
                <b>{{ row.code }}</b>
              </td>
              <td>{{ row.name }}</td>
              <td>{{ label(row.type) }}</td>
              <td>{{ label(row.normal_balance) }}</td>
              <td>{{ money(row.current_balance) }}</td>
              <td><StatusBadge :status="row.is_active ? 'active' : 'inactive'" /></td>
              <td class="row-actions">
                <span v-if="row.system_key" class="system-lock" title="Akun sistem terkunci"><i class="fas fa-lock"></i></span>
                <button v-else title="Edit akun" @click="openAccountForm(row)"><i class="fas fa-edit"></i></button>
              </td></template
            ></DataTable
          >
        </section>
        <section class="panel accounting-panel">
          <PanelTitle
            eyebrow="BUKU BESAR"
            title="Mutasi dan saldo akun"
            icon="fas fa-book-open"
          />
          <div class="ledger-controls">
            <select v-model="ledgerAccountId">
              <option value="">Pilih akun</option>
              <option
                v-for="account in masters.accounts"
                :key="account.id"
                :value="account.id"
              >
                {{ account.code }} · {{ account.name }}
              </option></select
            ><button
              class="btn-main"
              :disabled="!ledgerAccountId"
              @click="loadLedger"
            >
              Tampilkan
            </button>
          </div>
          <div v-if="ledger.account" class="ledger-balance">
            <span
              >Saldo awal <b>{{ money(ledger.opening_balance) }}</b></span
            ><span
              >Saldo akhir
              <strong>{{ money(ledger.closing_balance) }}</strong></span
            >
          </div>
          <DataTable
            :headers="[
              'Tanggal',
              'Jurnal',
              'Keterangan',
              'Debit',
              'Kredit',
              'Saldo',
            ]"
            :rows="ledger.rows || []"
            ><template #row="{ row }"
              ><td>{{ date(row.journal_date) }}</td>
              <td>{{ row.journal_number }}</td>
              <td>
                {{ row.description
                }}<small>{{ row.cost_center_name || "-" }}</small>
              </td>
              <td>{{ money(row.debit) }}</td>
              <td>{{ money(row.credit) }}</td>
              <td>
                <b>{{ money(row.balance) }}</b>
              </td></template
            ></DataTable
          >
        </section>
      </div>
      <div class="subsection-title">
        <div>
          <small>PERIODE AKUNTANSI</small>
          <h3>Riwayat Penutupan Periode</h3>
        </div>
      </div>
      <DataTable
        :headers="[
          'Nama Periode',
          'Tanggal Mulai',
          'Tanggal Selesai',
          'Status',
          'Ditutup Pada',
          'Aksi',
        ]"
        :rows="masters.periods"
        ><template #row="{ row }"
          ><td>
            <b>{{ row.name }}</b>
          </td>
          <td>{{ date(row.start_date) }}</td>
          <td>{{ date(row.end_date) }}</td>
          <td><StatusBadge :status="row.status" /></td>
          <td>{{ row.closed_at ? date(row.closed_at) : "-" }}</td>
          <td class="row-actions">
            <button
              v-if="row.status === 'closed'"
              title="Buka kembali periode"
              @click="openOperation('reopen-period', row)"
            >
              <i class="fas fa-lock-open"></i></button
            ><span v-else>-</span>
          </td></template
        ></DataTable
      >
    </template>

    <template
      v-else-if="activeTab === 'receivables' || activeTab === 'payables'"
    >
      <SectionHeader
        :title="
          activeTab === 'receivables'
            ? 'Piutang Pasien & Penjamin'
            : 'Utang Vendor'
        "
        :subtitle="
          activeTab === 'receivables'
            ? 'Pantau tagihan pasien, BPJS, asuransi, dan perusahaan'
            : 'Kelola invoice vendor, jatuh tempo, dan status pencocokan PO'
        "
        :button="
          activeTab === 'receivables'
            ? 'Tambah Piutang'
            : 'Tambah Tagihan Vendor'
        "
        @action="openSimpleForm"
      />
      <div class="aging-strip">
        <div v-for="a in agingRowsFor(activeTab)" :key="a.label">
          <small>{{ a.label }}</small
          ><b>{{ money(a.value) }}</b>
        </div>
      </div>
      <DataTable
        searchable
        v-if="activeTab === 'receivables'"
        :headers="[
          'Nomor',
          'Payer',
          'Tanggal',
          'Jatuh Tempo',
          'Nilai Awal',
          'Outstanding',
          'Status',
          'Aksi',
        ]"
        :rows="lists.receivables"
        ><template #row="{ row }"
          ><td>
            <b>{{ row.receivable_number }}</b>
          </td>
          <td>
            {{ row.payer_name }}<small>{{ label(row.payer_type) }}</small>
          </td>
          <td>{{ date(row.invoice_date) }}</td>
          <td>{{ date(row.due_date) }}</td>
          <td>{{ money(row.original_amount) }}</td>
          <td class="amount-due">{{ money(row.outstanding_amount) }}</td>
          <td><StatusBadge :status="row.status" /></td>
          <td class="row-actions">
            <button
              v-if="Number(row.outstanding_amount) > 0"
              title="Terima pembayaran"
              @click="openOperation('settle-receivable', row)"
            >
              <i class="fas fa-hand-holding-usd"></i>
            </button></td></template
      ></DataTable>
      <DataTable
        searchable
        v-else
        :headers="[
          'Nomor',
          'Vendor',
          'Invoice Vendor',
          'Jatuh Tempo',
          'Nilai Awal',
          'Outstanding',
          'Pembayaran',
          'Matching',
          'Aksi',
        ]"
        :rows="lists.payables"
        ><template #row="{ row }"
          ><td>
            <b>{{ row.payable_number }}</b>
          </td>
          <td>{{ row.vendor_name }}</td>
          <td>{{ row.vendor_invoice_number || "-" }}</td>
          <td>{{ date(row.due_date) }}</td>
          <td>{{ money(row.original_amount) }}</td>
          <td class="amount-due">{{ money(row.outstanding_amount) }}</td>
          <td><StatusBadge :status="row.status" /></td>
          <td><StatusBadge :status="row.matching_status" /></td>
          <td class="row-actions">
            <button
              v-if="Number(row.outstanding_amount) > 0"
              title="Bayar tagihan"
              @click="openOperation('settle-payable', row)"
            >
              <i class="fas fa-money-check-alt"></i>
            </button>
            <button
              title="Pencocokan PO"
              @click="openOperation('match-payable', row)"
            >
              <i class="fas fa-clipboard-check"></i>
            </button></td></template
      ></DataTable>
    </template>

    <template v-else-if="activeTab === 'cash'">
      <SectionHeader
        title="Kas, Bank & Petty Cash"
        subtitle="Satu ledger untuk kas masuk, kas keluar, transfer, settlement, dan reimbursement"
        button="Catat Transaksi"
        @action="openSimpleForm"
      />
      <div class="context-actions">
        <span
          ><i class="fas fa-info-circle"></i> Cocokkan saldo buku dengan
          rekening koran</span
        ><button class="btn-outline" @click="openCategoryManager()">
          <i class="fas fa-tags"></i> Kategori Pengeluaran
        </button><button class="btn-outline" @click="openOperation('reconcile')">
          <i class="fas fa-balance-scale"></i> Rekonsiliasi Bank
        </button>
      </div>
      <div class="cash-account-grid">
        <article v-for="account in cashAccounts" :key="account.id">
          <span :class="account.is_bank ? 'bank' : 'cash'"
            ><i
              :class="account.is_bank ? 'fas fa-university' : 'fas fa-wallet'"
            ></i
          ></span>
          <div>
            <small>{{ account.code }}</small
            ><b>{{ account.name }}</b
            ><strong>{{ money(account.current_balance) }}</strong>
          </div>
        </article>
      </div>
      <DataTable
        searchable
        :headers="[
          'Tanggal',
          'Nomor',
          'Jenis',
          'Keterangan',
          'Referensi',
          'Nominal',
          'Rekonsiliasi',
        ]"
        :rows="lists.cash"
        ><template #row="{ row }"
          ><td>{{ date(row.transaction_date) }}</td>
          <td>
            <b>{{ row.transaction_number }}</b>
          </td>
          <td>{{ label(row.type) }}</td>
          <td>{{ row.description }}</td>
          <td>{{ row.reference_number || "-" }}</td>
          <td>{{ money(row.amount) }}</td>
          <td>
            <StatusBadge
              :status="row.is_reconciled ? 'reconciled' : 'open'"
            /></td></template
      ></DataTable>
      <div class="subsection-title">
        <div>
          <small>KONTROL BANK</small>
          <h3>Riwayat Rekonsiliasi</h3>
        </div>
      </div>
      <DataTable
        searchable
        :headers="[
          'Tanggal Rekening Koran',
          'Akun Bank',
          'Saldo Buku',
          'Saldo Bank',
          'Selisih',
          'Status',
        ]"
        :rows="lists.reconciliations"
      >
        <template #row="{ row }"
          ><td>{{ date(row.statement_date) }}</td>
          <td>{{ accountName(row.finance_account_id) }}</td>
          <td>{{ money(row.book_balance) }}</td>
          <td>{{ money(row.statement_balance) }}</td>
          <td :class="Math.abs(Number(row.difference)) > 0 ? 'amount-due' : ''">
            {{ money(row.difference) }}
          </td>
          <td><StatusBadge :status="row.status" /></td
        ></template>
      </DataTable>
    </template>

    <template v-else-if="activeTab === 'reports'">
      <SectionHeader
        title="Laporan Keuangan"
        subtitle="Seluruh laporan bersumber dari jurnal posted"
      />
      <div class="report-controls">
        <select v-model="reportType" @change="loadReport">
          <option value="profit-loss">Laba Rugi</option>
          <option value="balance-sheet">Neraca</option>
          <option value="cash-flow">Arus Kas</option>
          <option value="trial-balance">Neraca Saldo</option>
          <option value="aging">Aging AR/AP</option>
          <option value="budget-vs-actual">Budget vs Actual</option>
          <option value="cost-center-profitability">
            Profitabilitas Cost Center
          </option>
          <option value="business-unit-profitability">
            Profitabilitas Klinik vs Apotek
          </option></select
        ><select v-if="dimensionFiltersEnabled" v-model="reportFilters.business_unit_id" @change="onReportUnitChange">
          <option value="">Semua unit usaha</option>
          <option v-for="unit in masters.business_units" :key="unit.id" :value="unit.id">{{ unit.name }}</option>
        </select
        ><select v-if="dimensionFiltersEnabled" v-model="reportFilters.cost_center_id">
          <option value="">Semua cost center</option>
          <option v-for="center in reportCostCenters" :key="center.id" :value="center.id">{{ center.code }} · {{ center.name }}</option>
        </select
        ><select v-if="dimensionFiltersEnabled" v-model="reportFilters.service_channel">
          <option value="">Semua kanal layanan</option>
          <option value="dental">Klinik Gigi</option>
          <option value="aesthetic">Klinik Kecantikan</option>
          <option value="maternal_child">Ibu &amp; Anak</option>
          <option value="mcu">Medical Check Up</option>
          <option value="eye">Klinik Mata</option>
          <option value="physiotherapy">Fisioterapi</option>
          <option value="occupational">Kesehatan Kerja</option>
          <option value="vaccination">Vaksinasi</option>
          <option value="home_care">Home Care</option>
        </select
        ><input type="date" v-model="period.from" /><input
          type="date"
          v-model="period.to"
        /><button class="btn-main" @click="loadReport">Tampilkan</button
        ><button class="btn-outline" @click="printReport">
          <i class="fas fa-print"></i> Cetak</button
        ><button class="btn-outline" @click="exportReport">
          <i class="fas fa-file-csv"></i> Ekspor CSV
        </button>
      </div>
      <section class="panel report-paper" id="finance-report">
        <div class="report-title">
          <small>LAPORAN KEUANGAN</small>
          <h2>{{ reportLabel }}</h2>
          <p>{{ date(period.from) }} – {{ date(period.to) }}</p>
        </div>
        <DataTable
          v-if="normalizedReportRows.length"
          :headers="reportHeaders"
          :rows="normalizedReportRows"
          ><template #row="{ row }"
            ><td>{{ row.display_code }}</td>
            <td>{{ row.display_name }}</td>
            <td>{{ money(row.display_debit) }}</td>
            <td>{{ money(row.display_credit) }}</td>
            <td>{{ money(row.display_balance) }}</td></template
          ></DataTable
        >
        <div v-else class="report-empty">
          <i class="fas fa-file-invoice-dollar"></i
          ><b>Belum ada transaksi pada periode ini</b
          ><span>Ubah rentang tanggal atau pilih jenis laporan lainnya.</span>
        </div>
        <div class="report-summary">
          <div v-for="r in reportSummary" :key="r.label">
            <span>{{ r.label }}</span
            ><b>{{ money(r.value) }}</b>
          </div>
        </div>
      </section>
    </template>

    <template v-else-if="activeTab === 'assets' || activeTab === 'budgets'">
      <SectionHeader
        :title="activeTab === 'assets' ? 'Aset Tetap' : 'Anggaran Klinik'"
        :subtitle="
          activeTab === 'assets'
            ? 'Aset, lokasi, penanggung jawab, dan penyusutan garis lurus'
            : 'Susun anggaran per akun dan cost center'
        "
        :button="activeTab === 'assets' ? 'Tambah Aset' : 'Buat Anggaran'"
        @action="openSimpleForm"
      />
      <div v-if="activeTab === 'assets'" class="context-actions">
        <span
          ><i class="fas fa-calculator"></i> Penyusutan garis lurus tercatat
          otomatis ke jurnal</span
        ><button class="btn-outline" @click="openOperation('depreciate')">
          <i class="fas fa-play"></i> Jalankan Penyusutan
        </button>
      </div>
      <DataTable
        searchable
        v-if="activeTab === 'assets'"
        :headers="[
          'Nomor',
          'Nama Aset',
          'Kategori',
          'Lokasi',
          'Harga Perolehan',
          'Akumulasi Penyusutan',
          'Status',
        ]"
        :rows="lists.assets"
        ><template #row="{ row }"
          ><td>
            <b>{{ row.asset_number }}</b>
          </td>
          <td>{{ row.name }}</td>
          <td>{{ row.category }}</td>
          <td>{{ row.location || "-" }}</td>
          <td>{{ money(row.acquisition_cost) }}</td>
          <td>{{ money(row.accumulated_depreciation) }}</td>
          <td><StatusBadge :status="row.status" /></td></template
      ></DataTable>
      <DataTable
        searchable
        v-else
        :headers="[
          'Nomor',
          'Nama Anggaran',
          'Periode Mulai',
          'Periode Selesai',
          'Status',
          'Aksi',
        ]"
        :rows="lists.budgets"
        ><template #row="{ row }"
          ><td>
            <b>{{ row.budget_number }}</b>
          </td>
          <td>{{ row.name }}</td>
          <td>{{ date(row.start_date) }}</td>
          <td>{{ date(row.end_date) }}</td>
          <td><StatusBadge :status="row.status" /></td>
          <td class="row-actions">
            <button title="Lihat realisasi anggaran" @click="showBudget(row)">
              <i class="fas fa-chart-pie"></i>
            </button>
            <button v-if="row.status === 'draft'" title="Setujui anggaran" @click="approveBudget(row)">
              <i class="fas fa-check"></i>
            </button></td></template
      ></DataTable>
    </template>

    <b-modal
      id="finance-entry-modal"
      size="lg"
      hide-footer
      centered
      :title="entryTitle"
    >
      <div class="entry-form">
        <template v-if="entryMode === 'receivables'">
          <label
            >Jenis pembayar<select v-model="entryForm.payer_type">
              <option value="patient">Pasien</option>
              <option value="bpjs">BPJS</option>
              <option value="insurance">Asuransi</option>
              <option value="corporate">Perusahaan</option>
            </select></label
          >
          <label
            >Nama pembayar<input
              v-model="entryForm.payer_name"
              placeholder="Nama pasien atau penjamin" /></label
          ><label
            >Tanggal invoice<input
              type="date"
              v-model="entryForm.invoice_date" /></label
          ><label
            >Jatuh tempo<input
              type="date"
              v-model="entryForm.due_date" /></label
          ><label
            >Nominal<input
              type="number"
              v-model.number="entryForm.amount" /></label
          ><label
            >Nomor klaim<input
              v-model="entryForm.claim_number"
              placeholder="Opsional"
          /></label>
        </template>
        <template v-else-if="entryMode === 'payables'">
          <label>Nama vendor<input v-model="entryForm.vendor_name" /></label
          ><label
            >Nomor invoice vendor<input
              v-model="entryForm.vendor_invoice_number" /></label
          ><label
            >Tanggal tagihan<input
              type="date"
              v-model="entryForm.bill_date" /></label
          ><label
            >Jatuh tempo<input
              type="date"
              v-model="entryForm.due_date" /></label
          ><label
            >Nominal<input
              type="number"
              v-model.number="entryForm.amount" /></label
          ><label
            >Status pencocokan<select v-model="entryForm.matching_status">
              <option value="unmatched">Belum dicocokkan</option>
              <option value="matched">
                PO, penerimaan, dan invoice sesuai
              </option>
              <option value="exception">Ada perbedaan</option>
            </select></label
          ><label
            >Jenis tagihan<select v-model="entryForm.expense_account_key">
              <option value="expense_operating">Beban Operasional</option>
              <option value="inventory_medicine">
                Persediaan Obat / Alkes
              </option>
              <option value="fixed_asset">Perolehan Aset Tetap</option>
            </select></label
          ><label>Unit usaha<select v-model="entryForm.finance_business_unit_id" @change="syncEntryCostCenter">
              <option value="">Pilih unit usaha</option><option v-for="unit in masters.business_units" :key="unit.id" :value="unit.id">{{ unit.name }}</option>
            </select></label
          ><label>Cost center<select v-model="entryForm.finance_cost_center_id">
              <option value="">Pilih cost center</option><option v-for="center in entryCostCenters" :key="center.id" :value="center.id">{{ center.code }} · {{ center.name }}</option>
            </select></label
          >
        </template>
        <template v-else-if="entryMode === 'cash'">
          <label
            >Tanggal<input
              type="date"
              v-model="entryForm.transaction_date" /></label
          ><label
            >Jenis<select v-model="entryForm.type">
              <option value="receipt">Kas masuk</option>
              <option value="payment">Kas keluar</option>
              <option value="topup">Top up petty cash</option>
              <option value="reimbursement">Reimbursement</option>
              <option value="transfer_out">Transfer keluar</option>
              <option value="transfer_in">Transfer masuk</option>
            </select></label
          ><label
            >Akun kas/bank<select v-model="entryForm.finance_account_id">
              <option v-for="a in cashAccounts" :key="a.id" :value="a.id">
                {{ a.code }} · {{ a.name }}
              </option>
            </select></label
          ><label
            >Akun lawan<select v-model="entryForm.counter_account_id">
              <option value="">Pilih akun lawan</option>
              <option v-for="a in masters.accounts" :key="a.id" :value="a.id">
                {{ a.code }} · {{ a.name }}
              </option>
            </select></label
          ><label>Kategori<select v-model="entryForm.finance_expense_category_id" @change="applyExpenseCategory">
              <option value="">Tanpa preset kategori</option><option v-for="category in entryExpenseCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
            </select></label
          ><label>Unit usaha<select v-model="entryForm.finance_business_unit_id" @change="syncEntryCostCenter">
              <option value="">Pilih unit usaha</option><option v-for="unit in masters.business_units" :key="unit.id" :value="unit.id">{{ unit.name }}</option>
            </select></label
          ><label>Cost center<select v-model="entryForm.finance_cost_center_id">
              <option value="">Pilih cost center</option><option v-for="center in entryCostCenters" :key="center.id" :value="center.id">{{ center.code }} · {{ center.name }}</option>
            </select></label
          ><label
            >Nominal<input
              type="number"
              v-model.number="entryForm.amount" /></label
          ><label>Referensi<input v-model="entryForm.reference_number" /></label
          ><label class="entry-wide"
            >Keterangan<input v-model="entryForm.description"
          /></label>
        </template>
        <template v-else-if="entryMode === 'assets'">
          <label>Nama aset<input v-model="entryForm.name" /></label
          ><label
            >Kategori<input
              v-model="entryForm.category"
              placeholder="Alat medis, TI, kendaraan..." /></label
          ><label
            >Tanggal perolehan<input
              type="date"
              v-model="entryForm.acquisition_date" /></label
          ><label
            >Harga perolehan<input
              type="number"
              v-model.number="entryForm.acquisition_cost" /></label
          ><label
            >Nilai residu<input
              type="number"
              v-model.number="entryForm.residual_value" /></label
          ><label
            >Umur manfaat (bulan)<input
              type="number"
              v-model.number="entryForm.useful_life_months" /></label
          ><label>Lokasi<input v-model="entryForm.location" /></label
          ><label>Penanggung jawab<input v-model="entryForm.custodian" /></label
          ><label>Unit usaha<select v-model="entryForm.finance_business_unit_id" @change="syncEntryCostCenter">
              <option value="">Pilih unit usaha</option><option v-for="unit in masters.business_units" :key="unit.id" :value="unit.id">{{ unit.name }}</option>
            </select></label
          ><label
            >Cost Center<select
              v-model="entryForm.finance_cost_center_id"
            >
              <option value="">Pilih cost center</option>
              <option
                v-for="c in entryCostCenters"
                :key="c.id"
                :value="c.id"
              >
                {{ c.name }}
              </option>
            </select></label
          ><label
            >Sumber pembayaran<select v-model="entryForm.cash_account_key">
              <option value="bank_operating">Bank Operasional</option>
              <option value="cash_clinic">Kas Klinik</option>
              <option value="cash_pharmacy">Kas Apotek</option>
              <option value="petty_cash">Petty Cash</option>
            </select></label
          >
        </template>
        <template v-else-if="entryMode === 'budgets'">
          <label>Nama anggaran<input v-model="entryForm.name" /></label
          ><label
            >Status<select v-model="entryForm.status">
              <option value="draft">Draft</option>
              <option value="approved">Disetujui</option>
            </select></label
          ><label
            >Periode mulai<input
              type="date"
              v-model="entryForm.start_date" /></label
          ><label
            >Periode selesai<input type="date" v-model="entryForm.end_date"
          /></label>
          <div
            class="entry-wide budget-line"
            v-for="(l, i) in entryForm.lines"
            :key="i"
          >
            <select v-model="l.finance_account_id">
              <option value="">Akun anggaran</option>
              <option v-for="a in expenseAccounts" :key="a.id" :value="a.id">
                {{ a.code }} · {{ a.name }}
              </option></select
            ><select v-model="l.finance_cost_center_id">
              <option :value="null">Pilih cost center</option>
              <option
                v-for="c in masters.cost_centers"
                :key="c.id"
                :value="c.id"
              >
                {{ c.name }}
              </option></select
            ><input
              type="number"
              v-model.number="l.amount"
              placeholder="Nilai anggaran"
            /><button @click="entryForm.lines.splice(i, 1)">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <button
            class="btn-link entry-wide"
            @click="
              entryForm.lines.push({
                finance_account_id: '',
                finance_cost_center_id: costCenterId('ADM'),
                amount: 0,
              })
            "
          >
            <i class="fas fa-plus"></i> Tambah akun anggaran
          </button>
        </template>
        <label
          v-if="entryMode === 'receivables' || entryMode === 'payables'"
          class="entry-wide"
          >Catatan<textarea v-model="entryForm.notes"></textarea>
        </label>
      </div>
      <div class="modal-actions">
        <button
          class="btn-outline"
          @click="$bvModal.hide('finance-entry-modal')"
        >
          Batal</button
        ><button class="btn-main" @click="saveEntry">Simpan & Posting</button>
      </div>
    </b-modal>

    <b-modal id="finance-account-modal" hide-footer centered size="lg" modal-class="finance-modal">
      <template #modal-title>{{ accountForm.id ? 'Edit Akun' : 'Tambah Akun COA' }}</template>
      <div class="entry-form">
        <label>Kode Akun<input v-model.trim="accountForm.code" placeholder="Contoh: 6106" /></label>
        <label>Nama Akun<input v-model.trim="accountForm.name" placeholder="Nama akun" /></label>
        <label>Kelompok<select v-model="accountForm.type"><option value="asset">Aset</option><option value="liability">Liabilitas</option><option value="equity">Ekuitas</option><option value="revenue">Pendapatan</option><option value="expense">Beban</option></select></label>
        <label>Saldo Normal<select v-model="accountForm.normal_balance"><option value="debit">Debit</option><option value="credit">Kredit</option></select></label>
        <label>Akun Induk<select v-model="accountForm.parent_id"><option :value="null">Tanpa akun induk</option><option v-for="a in accountParents" :key="a.id" :value="a.id">{{ a.code }} · {{ a.name }}</option></select></label>
        <label>Status<select v-model="accountForm.is_active"><option :value="true">Aktif</option><option :value="false">Nonaktif</option></select></label>
        <label class="coa-check"><input type="checkbox" v-model="accountForm.is_cash" /> Akun kas</label>
        <label class="coa-check"><input type="checkbox" v-model="accountForm.is_bank" /> Akun bank</label>
      </div>
      <div class="modal-actions"><button class="btn-outline" @click="$bvModal.hide('finance-account-modal')">Batal</button><button class="btn-main" @click="saveAccount"><i class="fas fa-save"></i> Simpan Akun</button></div>
    </b-modal>

    <b-modal id="finance-category-modal" hide-footer centered size="xl" modal-class="finance-modal">
      <template #modal-title>Kategori Pengeluaran Terpadu</template>
      <div class="entry-form">
        <label>Kode<input v-model.trim="categoryForm.code" placeholder="Contoh: APT-UTILITAS" /></label>
        <label>Nama kategori<input v-model.trim="categoryForm.name" placeholder="Nama kategori" /></label>
        <label>Akun beban<select v-model="categoryForm.finance_account_id"><option value="">Pilih akun beban</option><option v-for="a in expenseAccounts" :key="a.id" :value="a.id">{{ a.code }} · {{ a.name }}</option></select></label>
        <label>Unit usaha<select v-model="categoryForm.finance_business_unit_id" @change="syncCategoryCostCenter"><option value="">Pilih unit usaha</option><option v-for="unit in masters.business_units" :key="unit.id" :value="unit.id">{{ unit.name }}</option></select></label>
        <label>Cost center<select v-model="categoryForm.finance_cost_center_id"><option value="">Pilih cost center</option><option v-for="center in categoryCostCenters" :key="center.id" :value="center.id">{{ center.code }} · {{ center.name }}</option></select></label>
        <label>Status<select v-model="categoryForm.is_active"><option :value="true">Aktif</option><option :value="false">Nonaktif</option></select></label>
      </div>
      <div class="modal-actions"><button class="btn-outline" @click="resetCategoryForm">Form Baru</button><button class="btn-main" @click="saveCategory"><i class="fas fa-save"></i> {{ categoryForm.id ? 'Perbarui' : 'Tambah' }} Kategori</button></div>
      <DataTable :headers="['Kode','Kategori','Unit','Cost Center','Status','Aksi']" :rows="masters.expense_categories || []" searchable>
        <template #row="{ row }"><td><b>{{ row.code }}</b></td><td>{{ row.name }}</td><td>{{ unitName(row.finance_business_unit_id) }}</td><td>{{ costCenterName(row.finance_cost_center_id) }}</td><td><StatusBadge :status="row.is_active ? 'active' : 'inactive'" /></td><td class="row-actions"><button title="Edit kategori" @click="openCategoryForm(row)"><i class="fas fa-edit"></i></button></td></template>
      </DataTable>
    </b-modal>

    <b-modal
      id="finance-operation-modal"
      hide-footer
      centered
      :title="operationTitle"
    >
      <div class="operation-form">
        <template
          v-if="
            operationMode === 'settle-receivable' ||
            operationMode === 'settle-payable'
          "
        >
          <div class="operation-summary">
            <span>Nomor</span
            ><b>{{
              operationRow.receivable_number || operationRow.payable_number
            }}</b
            ><span>Outstanding</span
            ><strong>{{ money(operationRow.outstanding_amount) }}</strong>
          </div>
          <label
            >Tanggal transaksi<input type="date" v-model="operationForm.date"
          /></label>
          <label
            >Nominal<input
              type="number"
              :max="operationRow.outstanding_amount"
              v-model.number="operationForm.amount"
          /></label>
          <label
            >Akun kas/bank<select v-model="operationForm.cash_account_key">
              <option value="bank_operating">Bank Operasional</option>
              <option value="cash_clinic">Kas Klinik</option>
              <option value="petty_cash">Petty Cash</option>
            </select></label
          >
        </template>
        <template v-else-if="operationMode === 'reconcile'">
          <label
            >Akun bank<select v-model="operationForm.finance_account_id">
              <option value="">Pilih akun bank</option>
              <option v-for="a in bankAccounts" :key="a.id" :value="a.id">
                {{ a.code }} · {{ a.name }}
              </option>
            </select></label
          >
          <label
            >Tanggal rekening koran<input
              type="date"
              v-model="operationForm.statement_date"
          /></label>
          <label
            >Saldo rekening koran<input
              type="number"
              v-model.number="operationForm.statement_balance"
          /></label>
          <label
            >Catatan<textarea v-model="operationForm.notes"></textarea>
          </label>
          <button
            class="btn-outline entry-wide"
            :disabled="
              !operationForm.finance_account_id || !operationForm.statement_date
            "
            @click="loadReconciliationCandidates"
          >
            <i class="fas fa-search-dollar"></i> Muat Transaksi Bank
          </button>
          <div
            v-if="reconciliationPreview.rows.length"
            class="entry-wide reconciliation-picker"
          >
            <div class="reconciliation-totals">
              <span
                >Saldo awal
                <b>{{ money(reconciliationPreview.opening_balance) }}</b></span
              ><span
                >Transaksi terpilih
                <b>{{ money(reconciliationSelectedAmount) }}</b></span
              ><span
                >Saldo kliring
                <b>{{ money(reconciliationClearedBalance) }}</b></span
              ><span
                :class="
                  Math.abs(reconciliationDifference) > 0.009
                    ? 'amount-due'
                    : 'amount-ok'
                "
                >Selisih <b>{{ money(reconciliationDifference) }}</b></span
              >
            </div>
            <div class="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Pilih</th>
                    <th>Tanggal</th>
                    <th>Referensi</th>
                    <th>Uraian</th>
                    <th>Masuk</th>
                    <th>Keluar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="line in reconciliationPreview.rows" :key="line.id">
                    <td>
                      <input
                        type="checkbox"
                        :value="line.id"
                        v-model="operationForm.journal_line_ids"
                      />
                    </td>
                    <td>{{ date(line.journal_date) }}</td>
                    <td>{{ line.reference_number || line.journal_number }}</td>
                    <td>{{ line.description }}</td>
                    <td>{{ money(line.debit) }}</td>
                    <td>{{ money(line.credit) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
        <template v-else-if="operationMode === 'close-period'">
          <div class="warning-box">
            <i class="fas fa-exclamation-triangle"></i
            ><span
              >Setelah ditutup, transaksi tidak dapat diposting ke periode
              ini.</span
            >
          </div>
          <label
            >Nama periode<input
              v-model="operationForm.name"
              placeholder="Contoh: Agustus 2026"
          /></label>
          <label
            >Mulai<input type="date" v-model="operationForm.start_date"
          /></label>
          <label
            >Selesai<input type="date" v-model="operationForm.end_date"
          /></label>
          <label
            >Catatan<textarea v-model="operationForm.notes"></textarea>
          </label>
        </template>
        <template v-else-if="operationMode === 'reopen-period'">
          <div class="warning-box">
            <i class="fas fa-user-shield"></i
            ><span
              >Pembukaan periode harus memiliki alasan audit yang jelas. Jurnal
              baru akan kembali diizinkan pada rentang ini.</span
            >
          </div>
          <div class="operation-summary">
            <span>Periode</span><b>{{ operationRow.name }}</b
            ><span>Rentang</span
            ><strong
              >{{ date(operationRow.start_date) }} –
              {{ date(operationRow.end_date) }}</strong
            >
          </div>
          <label
            >Alasan pembukaan kembali<textarea
              v-model="operationForm.reason"
              placeholder="Minimal 10 karakter"
            ></textarea>
          </label>
        </template>
        <template v-else-if="operationMode === 'depreciate'">
          <div class="warning-box info">
            <i class="fas fa-info-circle"></i
            ><span
              >Sistem hanya memproses aset aktif yang belum disusutkan untuk
              periode terkait.</span
            >
          </div>
          <label
            >Proses sampai tanggal<input
              type="date"
              v-model="operationForm.through_date"
          /></label>
        </template>
        <template v-else-if="operationMode === 'reverse'">
          <div class="operation-summary">
            <span>Jurnal</span><b>{{ operationRow.journal_number }}</b>
          </div>
          <label
            >Alasan reversal<textarea
              v-model="operationForm.reason"
              placeholder="Jelaskan alasan koreksi (minimal 5 karakter)"
            ></textarea>
          </label>
        </template>
        <template v-else-if="operationMode === 'match-payable'">
          <div class="operation-summary">
            <span>Tagihan</span><b>{{ operationRow.payable_number }}</b
            ><span>Vendor</span><strong>{{ operationRow.vendor_name }}</strong>
          </div>
          <label
            >Purchase Order<select v-model="operationForm.purchase_order_ref">
              <option value="">Tanpa PO</option>
              <option
                v-for="po in masters.purchase_orders"
                :key="po.source_type + '-' + po.id"
                :value="po.source_type + ':' + po.id"
              >
                {{ po.source_label }} · {{ po.po_no }} · {{ po.vendor_name }}
              </option>
            </select></label
          >
          <label
            >Status pencocokan<select v-model="operationForm.matching_status">
              <option value="matched">Sesuai</option>
              <option value="exception">Ada perbedaan</option>
              <option value="unmatched">Belum dicocokkan</option>
            </select></label
          >
          <label
            >Catatan matching<textarea
              v-model="operationForm.matching_notes"
              placeholder="Catat hasil verifikasi PO, penerimaan, dan invoice vendor"
            ></textarea>
          </label>
        </template>
      </div>
      <div class="modal-actions">
        <button
          class="btn-outline"
          @click="$bvModal.hide('finance-operation-modal')"
        >
          Batal</button
        ><button class="btn-main" @click="submitOperation">
          {{ operationSubmitLabel }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="finance-budget-detail"
      size="lg"
      hide-footer
      centered
      title="Realisasi Anggaran"
    >
      <template v-if="budgetDetail.budget"
        ><div class="journal-detail-head">
          <div>
            <small>ANGGARAN</small><b>{{ budgetDetail.budget.name }}</b>
          </div>
          <div>
            <small>TOTAL ANGGARAN</small
            ><b>{{ money(budgetDetail.total_budget) }}</b>
          </div>
          <div>
            <small>REALISASI</small
            ><b>{{ money(budgetDetail.total_actual) }}</b>
          </div>
        </div>
        <DataTable
          :headers="[
            'Akun',
            'Cost Center',
            'Anggaran',
            'Realisasi',
            'Sisa',
            'Terpakai',
          ]"
          :rows="budgetDetail.lines || []"
          ><template #row="{ row }"
            ><td>{{ row.account_code }} · {{ row.account_name }}</td>
            <td>{{ row.cost_center_name || "Semua unit" }}</td>
            <td>{{ money(row.amount) }}</td>
            <td>{{ money(row.actual) }}</td>
            <td :class="Number(row.variance) < 0 ? 'amount-due' : ''">
              {{ money(row.variance) }}
            </td>
            <td>
              <b>{{ row.utilization }}%</b>
            </td></template
          ></DataTable
        ></template
      >
    </b-modal>

    <b-modal
      id="finance-journal-detail"
      size="lg"
      hide-footer
      centered
      title="Detail Jurnal"
    >
      <div v-if="journalDetail.journal" class="journal-detail-head">
        <div>
          <small>NOMOR JURNAL</small
          ><b>{{ journalDetail.journal.journal_number }}</b>
        </div>
        <div>
          <small>TANGGAL</small
          ><b>{{ date(journalDetail.journal.journal_date) }}</b>
        </div>
        <div>
          <small>STATUS</small
          ><StatusBadge :status="journalDetail.journal.status" />
        </div>
      </div>
      <p class="journal-description">
        {{ journalDetail.journal && journalDetail.journal.description }}
      </p>
      <DataTable
        :headers="['Kode Akun', 'Nama Akun', 'Cost Center', 'Debit', 'Kredit']"
        :rows="journalDetail.lines || []"
        ><template #row="{ row }"
          ><td>{{ row.account_code }}</td>
          <td>{{ row.account_name }}</td>
          <td>{{ row.cost_center_name || "-" }}</td>
          <td>{{ money(row.debit) }}</td>
          <td>{{ money(row.credit) }}</td></template
        ></DataTable
      >
    </b-modal>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import WorkspaceTabs from "@/component/general/WorkspaceTabs.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
const PanelTitle = {
  props: ["eyebrow", "title", "icon"],
  template:
    '<div class="panel-title"><div><span>{{eyebrow}}</span><h3>{{title}}</h3></div><i :class="icon"></i></div>',
};
const SectionHeader = {
  props: ["title", "subtitle", "button"],
  template:
    '<div class="section-head"><div><h2>{{title}}</h2><p>{{subtitle}}</p></div><button v-if="button" class="btn-main" @click="$emit(\'action\')"><i class="fas fa-plus"></i> {{button}}</button></div>',
};
const StatusBadge = {
  props: ["status"],
  template:
    "<span :class=\"['status-badge',status]\">{{String(status||'-').replaceAll('_',' ')}}</span>",
};
const DataTable = {
  props: { headers: Array, rows: Array, searchable: Boolean },
  data() {
    return { search: "", page: 1, pageSize: 10 };
  },
  computed: {
    filtered() {
      const query = this.search.trim().toLowerCase();
      return query
        ? (this.rows || []).filter((row) =>
            JSON.stringify(row).toLowerCase().includes(query)
          )
        : this.rows || [];
    },
    pages() {
      return Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
    },
    visibleRows() {
      const validPage = Math.min(this.page, this.pages);
      return this.filtered.slice(
        (validPage - 1) * this.pageSize,
        validPage * this.pageSize
      );
    },
  },
  watch: {
    search() {
      this.page = 1;
    },
    rows() {
      if (this.page > this.pages) this.page = this.pages;
    },
  },
  template:
    '<section class="data-card"><div v-if="searchable" class="data-toolbar"><div><i class="fas fa-search"></i><input v-model="search" placeholder="Cari pada daftar ini..." /></div><span><i class="fas fa-database"></i> {{filtered.length}} data</span></div><div class="table-responsive"><table><thead><tr><th v-for="h in headers" :key="h">{{h}}</th></tr></thead><tbody><tr v-for="row in visibleRows" :key="row.id"><slot name="row" :row="row"></slot></tr><tr v-if="!visibleRows.length"><td :colspan="headers.length" class="empty">Belum ada data untuk ditampilkan</td></tr></tbody></table></div><div v-if="searchable && pages > 1" class="data-pagination"><button :disabled="page <= 1" @click="page--"><i class="fas fa-chevron-left"></i></button><span>Halaman <b>{{page}}</b> dari {{pages}}</span><button :disabled="page >= pages" @click="page++"><i class="fas fa-chevron-right"></i></button></div></section>',
};
export default {
  components: { WorkspaceTabs, PanelTitle, SectionHeader, StatusBadge, DataTable },
  data() {
    const now = new Date(),
      from = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      activeTab: this.$route.query.tab || "dashboard",
      tabs: [
        { id: "dashboard", label: "Dashboard", icon: "fas fa-chart-line" },
        { id: "journals", label: "Akuntansi", icon: "fas fa-book" },
        {
          id: "receivables",
          label: "Piutang",
          icon: "fas fa-file-invoice-dollar",
        },
        { id: "payables", label: "Utang", icon: "fas fa-file-invoice" },
        { id: "cash", label: "Kas & Bank", icon: "fas fa-university" },
        { id: "reports", label: "Laporan", icon: "fas fa-chart-bar" },
        { id: "assets", label: "Aset", icon: "fas fa-building" },
        { id: "budgets", label: "Anggaran", icon: "fas fa-coins" },
      ],
      dashboard: { aging: {}, business_units: [] },
      masters: { accounts: [], business_units: [], cost_centers: [], expense_categories: [], periods: [] },
      journals: [],
      lists: {
        receivables: [],
        payables: [],
        cash: [],
        reconciliations: [],
        assets: [],
        budgets: [],
      },
      period: { from: this.iso(from), to: this.iso(now) },
      showJournalForm: false,
      journalForm: {
        journal_date: this.iso(now),
        description: "",
        lines: [this.blankLine(), this.blankLine()],
      },
      reportType: "profit-loss",
      reportFilters: { business_unit_id: "", cost_center_id: "", service_channel: "" },
      report: {},
      entryMode: "",
      entryForm: {},
      operationMode: "",
      operationRow: {},
      operationForm: {},
      journalDetail: { journal: null, lines: [] },
      ledgerAccountId: "",
      ledger: { account: null, rows: [] },
      accountForm: {},
      categoryForm: {},
      budgetDetail: { budget: null, lines: [] },
      reconciliationPreview: { opening_balance: 0, rows: [] },
    };
  },
  computed: {
    metrics() {
      return [
        {
          label: "Saldo Kas & Bank",
          value: this.dashboard.cash_balance,
          icon: "fas fa-wallet",
          tone: "blue",
        },
        {
          label: "Total Piutang",
          value: this.dashboard.receivables,
          icon: "fas fa-hand-holding-usd",
          tone: "amber",
        },
        {
          label: "Total Utang",
          value: this.dashboard.payables,
          icon: "fas fa-file-invoice",
          tone: "red",
        },
        {
          label: "Pendapatan",
          value: this.dashboard.revenue,
          icon: "fas fa-arrow-up",
          tone: "green",
        },
        {
          label: "Beban",
          value: this.dashboard.expenses,
          icon: "fas fa-arrow-down",
          tone: "violet",
        },
        {
          label: "Laba Bersih",
          value: this.dashboard.net_profit,
          icon: "fas fa-chart-line",
          tone: "teal",
        },
      ];
    },
    agingRows() {
      return this.agingRowsFor("receivables");
    },
    journalTotals() {
      return {
        debit: this.journalForm.lines.reduce(
          (s, l) => s + Number(l.debit || 0),
          0
        ),
        credit: this.journalForm.lines.reduce(
          (s, l) => s + Number(l.credit || 0),
          0
        ),
      };
    },
    reportLabel() {
      return {
        "profit-loss": "Laba Rugi",
        "balance-sheet": "Neraca",
        "cash-flow": "Arus Kas",
        "trial-balance": "Neraca Saldo",
        aging: "Aging AR/AP",
        "budget-vs-actual": "Budget vs Actual",
        "cost-center-profitability": "Profitabilitas Cost Center",
        "business-unit-profitability": "Profitabilitas Klinik vs Apotek",
      }[this.reportType];
    },
    reportHeaders() {
      if (this.reportType === "cash-flow")
        return [
          "Tanggal",
          "Uraian",
          "Kas Masuk",
          "Kas Keluar",
          "Arus Kas Bersih",
        ];
      if (this.reportType === "aging")
        return ["Kelompok", "Umur Tagihan", "Piutang", "Utang", "Selisih"];
      if (this.reportType === "budget-vs-actual")
        return ["Kode", "Akun Anggaran", "Anggaran", "Aktual", "Sisa Anggaran"];
      if (this.reportType === "cost-center-profitability")
        return [
          "Unit",
          "Cost Center",
          "Beban",
          "Pendapatan",
          "Surplus / Defisit",
        ];
      if (this.reportType === "business-unit-profitability")
        return ["Unit Usaha", "Nama Unit", "Beban + HPP", "Pendapatan", "Laba Bersih"];
      return ["Kode", "Nama Akun", "Debit", "Kredit", "Saldo"];
    },
    normalizedReportRows() {
      if (this.reportType === "aging") {
        const ar = this.report.receivables || {},
          ap = this.report.payables || {};
        const buckets = [
          ["Belum jatuh tempo", "current"],
          ["1–30 hari", "1_30"],
          ["31–60 hari", "31_60"],
          ["61–90 hari", "61_90"],
          ["> 90 hari", "over_90"],
        ];
        return buckets.map((item, index) => ({
          id: index,
          display_code: "AGING",
          display_name: item[0],
          display_debit: Number(ar[item[1]] || 0),
          display_credit: Number(ap[item[1]] || 0),
          display_balance: Number(ar[item[1]] || 0) - Number(ap[item[1]] || 0),
        }));
      }
      return (this.report.rows || []).map((row, index) => {
        if (this.reportType === "cash-flow")
          return {
            id: row.id || index,
            display_code: this.date(row.transaction_date),
            display_name: row.description || "-",
            display_debit: row.cash_in,
            display_credit: row.cash_out,
            display_balance: row.amount,
          };
        if (this.reportType === "budget-vs-actual")
          return {
            id: row.id || index,
            display_code: row.code || "-",
            display_name: row.name || "-",
            display_debit: row.amount,
            display_credit: row.actual,
            display_balance: row.variance,
          };
        if (this.reportType === "cost-center-profitability")
          return {
            id: row.id || index,
            display_code: row.code || "-",
            display_name: row.name || "-",
            display_debit: row.debit,
            display_credit: row.credit,
            display_balance: row.variance,
          };
        if (this.reportType === "business-unit-profitability")
          return {
            id: row.id || index,
            display_code: row.code || "-",
            display_name: `${row.name || "-"} · Margin ${Number(row.margin_percent || 0).toFixed(2)}%`,
            display_debit: row.expense,
            display_credit: row.revenue,
            display_balance: row.net_profit,
          };
        const debit = Number(row.debit || 0),
          credit = Number(row.credit || 0);
        const natural =
          row.normal_balance === "credit" ? credit - debit : debit - credit;
        return {
          id: row.id || index,
          display_code: row.code || "-",
          display_name: row.name || "-",
          display_debit: debit,
          display_credit: credit,
          display_balance: natural,
        };
      });
    },
    reportSummary() {
      const map = [
        ["Total Pendapatan", "total_revenue"],
        ["Total Beban", "total_expense"],
        ["Laba Bersih", "net_profit"],
        ["Total Aset", "assets"],
        ["Total Liabilitas", "liabilities"],
        ["Total Ekuitas", "equity"],
        ["Kas Masuk", "cash_in"],
        ["Kas Keluar", "cash_out"],
        ["Total Debit", "total_debit"],
        ["Total Kredit", "total_credit"],
      ];
      return map
        .filter((x) => this.report[x[1]] !== undefined)
        .map((x) => ({ label: x[0], value: this.report[x[1]] }));
    },
    entryTitle() {
      return (
        {
          receivables: "Tambah Piutang",
          payables: "Tambah Tagihan Vendor",
          cash: "Catat Kas / Bank",
          assets: "Tambah Aset Tetap",
          budgets: "Buat Anggaran",
        }[this.entryMode] || "Transaksi Keuangan"
      );
    },
    cashAccounts() {
      return (this.masters.accounts || []).filter(
        (a) => a.is_cash || a.is_bank
      );
    },
    bankAccounts() {
      return (this.masters.accounts || []).filter((a) => a.is_bank);
    },
    operationTitle() {
      return (
        {
          "settle-receivable": "Terima Pembayaran Piutang",
          "settle-payable": "Bayar Tagihan Vendor",
          reconcile: "Rekonsiliasi Bank",
          "close-period": "Tutup Periode Akuntansi",
          depreciate: "Proses Penyusutan Aset",
          reverse: "Reverse Jurnal",
          "match-payable": "Pencocokan Tagihan Vendor",
          "reopen-period": "Buka Kembali Periode",
        }[this.operationMode] || "Proses Keuangan"
      );
    },
    operationSubmitLabel() {
      return (
        {
          "settle-receivable": "Terima Pembayaran",
          "settle-payable": "Bayar Tagihan",
          reconcile: "Proses Rekonsiliasi",
          "close-period": "Tutup Periode",
          depreciate: "Proses Penyusutan",
          reverse: "Posting Reversal",
          "match-payable": "Simpan Hasil Matching",
          "reopen-period": "Buka Periode",
        }[this.operationMode] || "Proses"
      );
    },
    expenseAccounts() {
      return (this.masters.accounts || []).filter((a) => a.type === "expense");
    },
    entryCostCenters() {
      const unitId = Number(this.entryForm.finance_business_unit_id || 0);
      return (this.masters.cost_centers || []).filter(
        (center) => !unitId || Number(center.finance_business_unit_id) === unitId
      );
    },
    entryExpenseCategories() {
      const unitId = Number(this.entryForm.finance_business_unit_id || 0);
      return (this.masters.expense_categories || []).filter(
        (category) => !unitId || !category.finance_business_unit_id || Number(category.finance_business_unit_id) === unitId
      );
    },
    reportCostCenters() {
      const unitId = Number(this.reportFilters.business_unit_id || 0);
      return (this.masters.cost_centers || []).filter(
        (center) => !unitId || Number(center.finance_business_unit_id) === unitId
      );
    },
    dimensionFiltersEnabled() {
      return ["profit-loss", "cost-center-profitability", "business-unit-profitability"].includes(this.reportType);
    },
    categoryCostCenters() {
      const unitId = Number(this.categoryForm.finance_business_unit_id || 0);
      return (this.masters.cost_centers || []).filter(
        (center) => !unitId || Number(center.finance_business_unit_id) === unitId
      );
    },
    accountParents() {
      return (this.masters.accounts || []).filter(
        (a) => a.is_active && Number(a.id) !== Number(this.accountForm.id)
      );
    },
    reconciliationSelectedAmount() {
      const selected = new Set(this.operationForm.journal_line_ids || []);
      return (this.reconciliationPreview.rows || [])
        .filter((r) => selected.has(r.id))
        .reduce((sum, r) => sum + Number(r.amount || 0), 0);
    },
    reconciliationClearedBalance() {
      return (
        Number(this.reconciliationPreview.opening_balance || 0) +
        this.reconciliationSelectedAmount
      );
    },
    reconciliationDifference() {
      return (
        Number(this.operationForm.statement_balance || 0) -
        this.reconciliationClearedBalance
      );
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Keuangan" },
      { title: "Finance Center" },
    ]);
    this.initialize();
  },
  watch: {
    "$route.query.tab": {
      immediate: true,
      handler(tab) {
        const valid = this.tabs.some((item) => item.id === tab);
        this.activeTab = valid ? tab : "dashboard";
        if (this.activeTab === "reports" && this.reportType) this.loadReport();
      },
    },
  },
  methods: {
    iso(d) {
      return d.toISOString().slice(0, 10);
    },
    blankLine() {
      return {
        finance_account_id: "",
        finance_cost_center_id: null,
        debit: 0,
        credit: 0,
      };
    },
    async initialize() {
      try {
        await ApiService.post("finance/initialize", {});
        await this.loadAll();
        if (this.$route.query.report) this.reportType = this.$route.query.report;
        if (this.$route.query.unit) {
          this.reportFilters.business_unit_id = this.unitId(
            String(this.$route.query.unit).toUpperCase()
          );
        }
        if (this.$route.query.cost_center) {
          this.reportFilters.cost_center_id = this.costCenterId(
            String(this.$route.query.cost_center).toUpperCase()
          );
        }
        if (this.$route.query.service_channel) {
          this.reportFilters.service_channel = String(this.$route.query.service_channel);
        }
        if (this.activeTab === "reports") await this.loadReport();
      } catch (e) {
        this.toastError(e);
      }
    },
    async loadAll() {
      try {
        const [d, m, j, ...lists] = await Promise.all([
          ApiService.get(
            `finance/dashboard?from=${this.period.from}&to=${this.period.to}`
          ),
          ApiService.get("finance/masters"),
          ApiService.get(
            `finance/journals?from=${this.period.from}&to=${this.period.to}`
          ),
          ...[
            "receivables",
            "payables",
            "cash",
            "reconciliations",
            "assets",
            "budgets",
          ].map((x) => ApiService.get(`finance/lists/${x}`)),
        ]);
        this.dashboard = d.data.data || d.data;
        this.masters = m.data.data || m.data;
        this.journals = j.data.data || j.data;
        [
          "receivables",
          "payables",
          "cash",
          "reconciliations",
          "assets",
          "budgets",
        ].forEach(
          (x, i) => (this.lists[x] = lists[i].data.data || lists[i].data)
        );
        if (this.activeTab === "reports") this.loadReport();
      } catch (e) {
        this.toastError(e);
      }
    },
    selectTab(tab) {
      this.activeTab = tab;
      this.$router.replace({ query: { tab } }).catch(() => {});
      if (tab === "reports") this.loadReport();
    },
    async saveJournal() {
      try {
        await ApiService.post("finance/journals", this.journalForm);
        this.journalForm = {
          journal_date: this.iso(new Date()),
          description: "",
          lines: [this.blankLine(), this.blankLine()],
        };
        this.showJournalForm = false;
        await this.loadAll();
        this.toast("Jurnal berhasil diposting.");
      } catch (e) {
        this.toastError(e);
      }
    },
    openAccountForm(account = null) {
      this.accountForm = account
        ? {
            id: account.id,
            code: account.code,
            name: account.name,
            type: account.type,
            normal_balance: account.normal_balance,
            parent_id: account.parent_id || null,
            is_cash: Boolean(account.is_cash),
            is_bank: Boolean(account.is_bank),
            is_active: Boolean(account.is_active),
          }
        : {
            code: "",
            name: "",
            type: "expense",
            normal_balance: "debit",
            parent_id: null,
            is_cash: false,
            is_bank: false,
            is_active: true,
          };
      this.$bvModal.show("finance-account-modal");
    },
    async saveAccount() {
      try {
        if (!this.accountForm.code || !this.accountForm.name) {
          this.toastError({ message: "Kode dan nama akun wajib diisi." });
          return;
        }
        if (this.accountForm.id)
          await ApiService.put(
            `finance/accounts/${this.accountForm.id}`,
            this.accountForm
          );
        else await ApiService.post("finance/accounts", this.accountForm);
        this.$bvModal.hide("finance-account-modal");
        await this.loadAll();
        this.toast("Master akun berhasil disimpan.");
      } catch (e) {
        this.toastError(e);
      }
    },
    openCategoryManager() {
      this.resetCategoryForm();
      this.$bvModal.show("finance-category-modal");
    },
    openCategoryForm(category) {
      this.categoryForm = {
        id: category.id,
        code: category.code,
        name: category.name,
        finance_account_id: category.finance_account_id,
        finance_business_unit_id: category.finance_business_unit_id,
        finance_cost_center_id: category.finance_cost_center_id,
        is_active: Boolean(category.is_active),
      };
    },
    resetCategoryForm() {
      this.categoryForm = {
        code: "",
        name: "",
        finance_account_id: "",
        finance_business_unit_id: this.unitId("CLINIC"),
        finance_cost_center_id: this.costCenterId("ADM"),
        is_active: true,
      };
    },
    syncCategoryCostCenter() {
      if (!this.categoryCostCenters.some((center) => Number(center.id) === Number(this.categoryForm.finance_cost_center_id)))
        this.categoryForm.finance_cost_center_id = this.categoryCostCenters.length ? this.categoryCostCenters[0].id : "";
    },
    async saveCategory() {
      try {
        const form = this.categoryForm;
        if (!form.code || !form.name || !form.finance_account_id || !form.finance_business_unit_id || !form.finance_cost_center_id) {
          this.toastError({ message: "Kode, nama, akun beban, unit usaha, dan cost center wajib diisi." });
          return;
        }
        if (form.id) await ApiService.put(`finance/expense-categories/${form.id}`, form);
        else await ApiService.post("finance/expense-categories", form);
        const response = await ApiService.get("finance/masters");
        this.masters = response.data.data || response.data;
        this.resetCategoryForm();
        this.toast("Kategori pengeluaran berhasil disimpan.");
      } catch (e) { this.toastError(e); }
    },
    async loadReport() {
      try {
        const filters = [
          this.reportFilters.business_unit_id
            ? `business_unit_id=${this.reportFilters.business_unit_id}`
            : "",
          this.reportFilters.cost_center_id
            ? `cost_center_id=${this.reportFilters.cost_center_id}`
            : "",
          this.reportFilters.service_channel
            ? `service_channel=${encodeURIComponent(this.reportFilters.service_channel)}`
            : "",
        ]
          .filter(Boolean)
          .join("&");
        const r = await ApiService.get(
          `finance/reports/${this.reportType}?from=${this.period.from}&to=${this.period.to}${
            filters ? `&${filters}` : ""
          }`
        );
        this.report = r.data.data || r.data;
      } catch (e) {
        this.toastError(e);
      }
    },
    agingRowsFor(type) {
      const data = (this.dashboard.aging || {})[type] || {};
      const labels = [
          ["Belum jatuh tempo", "current"],
          ["1–30 hari", "1_30"],
          ["31–60 hari", "31_60"],
          ["61–90 hari", "61_90"],
          ["> 90 hari", "over_90"],
        ],
        max = Math.max(1, ...Object.values(data).map(Number));
      return labels.map((x) => ({
        label: x[0],
        value: Number(data[x[1]] || 0),
        width: (Number(data[x[1]] || 0) / max) * 100,
      }));
    },
    openSimpleForm() {
      this.entryMode = this.activeTab;
      const today = this.iso(new Date());
      if (this.entryMode === "receivables")
        this.entryForm = {
          payer_type: "patient",
          payer_name: "",
          invoice_date: today,
          due_date: today,
          amount: 0,
          claim_number: "",
          notes: "",
        };
      if (this.entryMode === "payables")
        this.entryForm = {
          vendor_name: "",
          vendor_invoice_number: "",
          bill_date: today,
          due_date: today,
          amount: 0,
          matching_status: "unmatched",
          expense_account_key: "expense_operating",
          finance_business_unit_id: this.unitId("CLINIC"),
          finance_cost_center_id: this.costCenterId("ADM"),
          notes: "",
        };
      if (this.entryMode === "cash")
        this.entryForm = {
          transaction_date: today,
          type: "payment",
          finance_account_id: "",
          counter_account_id: "",
          finance_expense_category_id: "",
          finance_business_unit_id: this.unitId("CLINIC"),
          finance_cost_center_id: this.costCenterId("ADM"),
          amount: 0,
          reference_number: "",
          description: "",
        };
      if (this.entryMode === "assets")
        this.entryForm = {
          name: "",
          category: "",
          acquisition_date: today,
          acquisition_cost: 0,
          residual_value: 0,
          useful_life_months: 60,
          location: "",
          custodian: "",
          finance_business_unit_id: this.unitId("CLINIC"),
          finance_cost_center_id: this.costCenterId("ADM"),
          cash_account_key: "bank_operating",
        };
      if (this.entryMode === "budgets")
        this.entryForm = {
          name: "",
          start_date: this.period.from,
          end_date: this.period.to,
          status: "approved",
          lines: [
            { finance_account_id: "", finance_cost_center_id: this.costCenterId("ADM"), amount: 0 },
          ],
        };
      this.$bvModal.show("finance-entry-modal");
    },
    unitId(code) {
      const unit = (this.masters.business_units || []).find(
        (item) => item.code === code
      );
      return unit ? unit.id : "";
    },
    costCenterId(code) {
      const center = (this.masters.cost_centers || []).find(
        (item) => item.code === code
      );
      return center ? center.id : "";
    },
    syncEntryCostCenter() {
      const valid = this.entryCostCenters.some(
        (center) => Number(center.id) === Number(this.entryForm.finance_cost_center_id)
      );
      if (!valid)
        this.entryForm.finance_cost_center_id = this.entryCostCenters.length
          ? this.entryCostCenters[0].id
          : "";
      const categoryValid = this.entryExpenseCategories.some(
        (category) => Number(category.id) === Number(this.entryForm.finance_expense_category_id)
      );
      if (!categoryValid) this.entryForm.finance_expense_category_id = "";
    },
    applyExpenseCategory() {
      const category = (this.masters.expense_categories || []).find(
        (item) => Number(item.id) === Number(this.entryForm.finance_expense_category_id)
      );
      if (!category) return;
      this.entryForm.counter_account_id = category.finance_account_id;
      this.entryForm.finance_business_unit_id = category.finance_business_unit_id;
      this.entryForm.finance_cost_center_id = category.finance_cost_center_id;
    },
    onReportUnitChange() {
      const valid = this.reportCostCenters.some(
        (center) => Number(center.id) === Number(this.reportFilters.cost_center_id)
      );
      if (!valid) this.reportFilters.cost_center_id = "";
      this.loadReport();
    },
    async saveEntry() {
      const urls = {
        receivables: "finance/receivables",
        payables: "finance/payables",
        cash: "finance/cash-transactions",
        assets: "finance/assets",
        budgets: "finance/budgets",
      };
      try {
        await ApiService.post(urls[this.entryMode], this.entryForm);
        this.$bvModal.hide("finance-entry-modal");
        await this.loadAll();
        this.toast(
          "Transaksi berhasil disimpan dan diposting ke Finance Core."
        );
      } catch (e) {
        this.toastError(e);
      }
    },
    openOperation(mode, row = {}) {
      const today = this.iso(new Date());
      this.operationMode = mode;
      this.operationRow = row;
      if (mode === "settle-receivable" || mode === "settle-payable")
        this.operationForm = {
          date: today,
          amount: Number(row.outstanding_amount || 0),
          cash_account_key: "bank_operating",
        };
      if (mode === "reconcile")
        this.operationForm = {
          finance_account_id: "",
          statement_date: today,
          statement_balance: 0,
          notes: "",
          journal_line_ids: [],
        };
      if (mode === "reconcile")
        this.reconciliationPreview = { opening_balance: 0, rows: [] };
      if (mode === "close-period")
        this.operationForm = {
          name: "",
          start_date: this.period.from,
          end_date: this.period.to,
          notes: "",
        };
      if (mode === "reopen-period") this.operationForm = { reason: "" };
      if (mode === "depreciate") this.operationForm = { through_date: today };
      if (mode === "reverse") this.operationForm = { reason: "" };
      if (mode === "match-payable")
        this.operationForm = {
          purchase_order_id: row.purchase_order_id || null,
          purchase_order_type: row.purchase_order_type || null,
          purchase_order_ref:
            row.purchase_order_id && row.purchase_order_type
              ? `${row.purchase_order_type}:${row.purchase_order_id}`
              : "",
          matching_status: row.matching_status || "unmatched",
          matching_notes: row.matching_notes || "",
        };
      this.$bvModal.show("finance-operation-modal");
    },
    async submitOperation() {
      const urls = {
        "settle-receivable": `finance/receivables/${this.operationRow.id}/settle`,
        "settle-payable": `finance/payables/${this.operationRow.id}/settle`,
        reconcile: "finance/bank-reconciliations",
        "close-period": "finance/periods/close",
        depreciate: "finance/assets/depreciate",
        reverse: `finance/journals/${this.operationRow.id}/reverse`,
        "match-payable": `finance/payables/${this.operationRow.id}/matching`,
        "reopen-period": `finance/periods/${this.operationRow.id}/reopen`,
      };
      try {
        if (
          this.operationMode === "match-payable" &&
          this.operationForm.purchase_order_ref
        ) {
          const [type, id] = this.operationForm.purchase_order_ref.split(":");
          this.operationForm.purchase_order_type = type;
          this.operationForm.purchase_order_id = Number(id);
        }
        const response =
          this.operationMode === "match-payable"
            ? await ApiService.put(urls[this.operationMode], this.operationForm)
            : await ApiService.post(
                urls[this.operationMode],
                this.operationForm
              );
        this.$bvModal.hide("finance-operation-modal");
        await this.loadAll();
        const result = response.data.data || response.data;
        this.toast(
          this.operationMode === "depreciate"
            ? `${result.processed || 0} aset berhasil diproses.`
            : "Proses keuangan berhasil disimpan."
        );
      } catch (e) {
        this.toastError(e);
      }
    },
    async showJournal(row) {
      try {
        const response = await ApiService.get(`finance/journals/${row.id}`);
        this.journalDetail = response.data.data || response.data;
        this.$bvModal.show("finance-journal-detail");
      } catch (e) {
        this.toastError(e);
      }
    },
    async loadLedger() {
      try {
        const response = await ApiService.get(
          `finance/ledger?account_id=${this.ledgerAccountId}&from=${this.period.from}&to=${this.period.to}`
        );
        this.ledger = response.data.data || response.data;
      } catch (e) {
        this.toastError(e);
      }
    },
    async showBudget(row) {
      try {
        const response = await ApiService.get(`finance/budgets/${row.id}`);
        this.budgetDetail = response.data.data || response.data;
        this.$bvModal.show("finance-budget-detail");
      } catch (e) {
        this.toastError(e);
      }
    },
    async approveBudget(row) {
      try {
        await ApiService.post(`finance/budgets/${row.id}/approve`, {});
        await this.loadAll();
        this.toast("Anggaran berhasil disetujui dan dikunci sebagai baseline.");
      } catch (e) {
        this.toastError(e);
      }
    },
    async loadReconciliationCandidates() {
      try {
        const response = await ApiService.get(
          `finance/bank-reconciliations/candidates?finance_account_id=${this.operationForm.finance_account_id}&statement_date=${this.operationForm.statement_date}`
        );
        this.reconciliationPreview = response.data.data || response.data;
        this.operationForm.journal_line_ids = (
          this.reconciliationPreview.rows || []
        ).map((row) => row.id);
      } catch (e) {
        this.toastError(e);
      }
    },
    exportReport() {
      const headers = this.reportHeaders;
      const rows = this.normalizedReportRows.map((r) => [
        r.display_code,
        r.display_name,
        r.display_debit,
        r.display_credit,
        r.display_balance,
      ]);
      const csv = [headers, ...rows]
        .map((row) =>
          row
            .map(
              (value) =>
                `"${String(value == null ? "" : value).replace(/"/g, '""')}"`
            )
            .join(";")
        )
        .join("\n");
      const blob = new Blob(["\ufeff" + csv], {
        type: "text/csv;charset=utf-8",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${this.reportLabel}-${this.period.from}-${this.period.to}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    printReport() {
      window.print();
    },
    money(v) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }).format(Number(v || 0));
    },
    date(v) {
      return v
        ? new Date(v).toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "-";
    },
    label(v) {
      return String(v || "-")
        .replaceAll("_", " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    accountName(id) {
      const account = (this.masters.accounts || []).find(
        (item) => Number(item.id) === Number(id)
      );
      return account ? `${account.code} · ${account.name}` : "-";
    },
    unitName(id) {
      const unit = (this.masters.business_units || []).find(
        (item) => Number(item.id) === Number(id)
      );
      return unit ? unit.name : "-";
    },
    costCenterName(id) {
      const center = (this.masters.cost_centers || []).find(
        (item) => Number(item.id) === Number(id)
      );
      return center ? `${center.code} · ${center.name}` : "-";
    },
    toast(m) {
      this.$bvToast.toast(m, {
        title: "Berhasil",
        variant: "success",
        solid: true,
      });
    },
    toastError(e) {
      this.$bvToast.toast(
        (e.response && e.response.data && e.response.data.message) ||
          "Data keuangan belum dapat diproses.",
        { title: "Periksa kembali", variant: "danger", solid: true }
      );
    },
  },
};
</script>

<style scoped>
.finance-page {
  padding-bottom: 32px;
  color: #26364a;
}
.btn-main,
.btn-outline {
  min-height: 40px;
  padding: 9px 14px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 750;
}
.entry-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.entry-form label {
  margin: 0;
  color: #596d83;
  font-size: 10px;
  font-weight: 750;
}
.entry-form input,
.entry-form select,
.entry-form textarea,
.budget-line input,
.budget-line select {
  width: 100%;
  min-height: 40px;
  margin-top: 5px;
  padding: 9px 10px;
  border: 1px solid #d4e1e9;
  border-radius: 8px;
  background: #fff;
  color: #293b50;
}
.entry-form textarea {
  min-height: 70px;
}
.entry-wide {
  grid-column: 1/-1;
}
.budget-line {
  display: grid;
  grid-template-columns: 1.4fr 1fr 0.8fr 34px;
  gap: 7px;
}
.budget-line button {
  margin-top: 5px;
  border: 0;
  border-radius: 8px;
  background: #fdebed;
  color: #c83d47;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 18px;
  padding-top: 13px;
  border-top: 1px solid #e6edf2;
}
.btn-main {
  border: 0;
  background: linear-gradient(105deg, #154d80, #15948c);
  color: #fff;
}
.btn-outline {
  border: 1px solid #cadbe7;
  background: #fff;
  color: #155985;
}
.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.metric-grid article,
.panel,
.data-card {
  background: #fff;
  border: 1px solid #dbe6ee;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(33, 63, 94, 0.05);
}
.metric-grid article {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
}
.metric-grid article > span {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 11px;
}
.metric-grid .blue {
  background: #e8f2fc;
  color: #17649b;
}
.metric-grid .amber {
  background: #fff2de;
  color: #b56d00;
}
.metric-grid .red {
  background: #fdebed;
  color: #cc4149;
}
.metric-grid .green {
  background: #e5f7ee;
  color: #158255;
}
.metric-grid .violet {
  background: #f0eafd;
  color: #714ec1;
}
.metric-grid .teal {
  background: #e5f6f4;
  color: #158d87;
}
.metric-grid small,
.metric-grid strong {
  display: block;
}
.metric-grid small {
  color: #718297;
  font-size: 10px;
}
.metric-grid strong {
  margin-top: 3px;
  font-size: 16px;
}
.business-unit-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 13px;
  margin-top: 13px;
}
.business-unit-grid h3 {
  margin: 2px 0 12px;
  color: #26445f;
}
.business-unit-grid small {
  color: #15948c;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
}
.unit-margin {
  margin-top: 10px;
  padding: 7px 9px;
  border-radius: 8px;
  background: #e8f5f4;
  color: #137d78;
  font-size: 11px;
  font-weight: 800;
  text-align: right;
}
.two-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
  margin-top: 13px;
}
.panel {
  padding: 18px;
}
.panel-title,
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.panel-title {
  margin-bottom: 12px;
}
.panel-title span {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: #168e88;
}
.panel-title h3,
.section-head h2 {
  margin: 2px 0 0;
  font-size: 16px;
}
.panel-title > i {
  color: #168c88;
  font-size: 21px;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #edf2f5;
}
.summary-row.total {
  font-size: 15px;
  color: #145584;
}
.balance-alert {
  margin-top: 12px;
  padding: 10px;
  border-radius: 9px;
  font-size: 11px;
}
.balance-alert.ok {
  background: #e9f7ef;
  color: #168153;
}
.balance-alert.warn {
  background: #fff2e2;
  color: #a96700;
}
.aging-bars > div {
  display: grid;
  grid-template-columns: 105px 1fr 105px;
  gap: 8px;
  align-items: center;
  margin: 10px 0;
  font-size: 10px;
}
.aging-bars > div > div {
  height: 7px;
  background: #edf2f5;
  border-radius: 99px;
  overflow: hidden;
}
.aging-bars i {
  display: block;
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #168f8a, #17669a);
}
.aging-bars b {
  text-align: right;
}
.section-head {
  margin: 18px 2px 12px;
}
.section-head p {
  margin: 3px 0 0;
  color: #718398;
  font-size: 11px;
}
.form-panel {
  margin-bottom: 13px;
}
.form-grid {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 10px;
}
.form-grid label,
.journal-line {
  font-size: 10px;
  color: #607288;
  font-weight: 700;
}
.journal-line {
  display: grid;
  grid-template-columns: 1.5fr 1.2fr 0.7fr 0.7fr 34px;
  gap: 7px;
  margin-top: 8px;
}
.journal-line input,
.journal-line select,
.form-grid input,
.report-controls input,
.report-controls select {
  width: 100%;
  min-height: 38px;
  padding: 8px 10px;
  border: 1px solid #d4e1e9;
  border-radius: 8px;
  background: #fff;
  color: #2b3b4f;
}
.journal-line button {
  border: 0;
  background: #fdebed;
  color: #c93e47;
  border-radius: 8px;
}
.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}
.btn-link {
  border: 0;
  background: transparent;
  color: #167d8c;
  font-weight: 700;
  margin-right: auto;
}
.data-card {
  overflow: hidden;
}
.table-responsive {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th {
  padding: 11px 13px;
  background: #f3f7fa;
  color: #60738b;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: left;
}
td {
  padding: 12px 13px;
  border-top: 1px solid #e8eef3;
  color: #34465d;
  font-size: 11px;
}
td small {
  display: block;
  color: #8392a4;
}
.empty {
  text-align: center;
  padding: 38px;
  color: #8797a8;
}
.data-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #e4edf2;
  background: #fbfdfe;
}
.data-toolbar > div {
  position: relative;
  width: min(360px, 75%);
}
.data-toolbar > div i {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: #168d87;
}
.data-toolbar input {
  width: 100%;
  min-height: 38px;
  padding: 8px 11px 8px 34px;
  border: 1px solid #d6e3eb;
  border-radius: 9px;
  color: #31475d;
}
.data-toolbar > span {
  color: #687d91;
  font-size: 10px;
  font-weight: 750;
  white-space: nowrap;
}
.data-toolbar > span i {
  margin-right: 5px;
  color: #15948c;
}
.data-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 10px 12px;
  border-top: 1px solid #e5edf2;
}
.data-pagination button {
  width: 34px;
  height: 34px;
  border: 1px solid #d4e2ea;
  border-radius: 8px;
  background: #fff;
  color: #126992;
  font-weight: 800;
}
.data-pagination button:disabled {
  opacity: 0.42;
}
.data-pagination span {
  color: #6d8093;
  font-size: 10px;
}

/* DataTable dirender sebagai child component, jadi aturan tabel perlu menembus
   scoped boundary tanpa memengaruhi tabel di modul lain. */
.finance-page :deep(.data-card) {
  overflow: hidden;
  background: #fff;
  border: 1px solid #dbe6ee;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(33, 63, 94, 0.05);
}
.finance-page :deep(.table-responsive) {
  overflow-x: auto;
}
.finance-page :deep(.data-card table) {
  width: 100%;
  border-collapse: collapse;
}
.finance-page :deep(.data-card th) {
  padding: 11px 16px;
  background: #f3f7fa;
  color: #60738b;
  font-size: 10px;
  font-weight: 800;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}
.finance-page :deep(.data-card td) {
  padding: 12px 16px;
  border-top: 1px solid #e8eef3;
  color: #34465d;
  font-size: 11px;
  vertical-align: middle;
}
.finance-page :deep(.data-toolbar) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid #e4edf2;
  background: #fbfdfe;
}
.finance-page :deep(.data-toolbar > div) {
  position: relative;
  width: min(360px, 75%);
}
.finance-page :deep(.data-toolbar > div i) {
  position: absolute;
  top: 50%;
  left: 12px;
  z-index: 1;
  transform: translateY(-50%);
  color: #168d87;
}
.finance-page :deep(.data-toolbar input) {
  width: 100%;
  min-height: 38px;
  padding: 8px 12px 8px 34px;
  border: 1px solid #d4e1e9;
  border-radius: 9px;
  background: #fff;
  color: #2b3b4f;
}
.finance-page :deep(.data-toolbar > span) {
  color: #66798e;
  font-size: 10px;
  font-weight: 750;
  white-space: nowrap;
}
.coa-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #eef7f7;
  color: #567083;
  font-size: 10px;
}
.coa-toolbar span i { color: #168d87; margin-right: 6px; }
.coa-toolbar .btn-main { flex: none; }
.system-lock {
  display: inline-grid;
  place-items: center;
  width: 31px;
  height: 31px;
  border-radius: 8px;
  background: #eef2f6;
  color: #718297;
}
.coa-check { display: flex; align-items: center; gap: 9px; min-height: 40px; margin-top: 17px !important; padding: 8px 11px; border: 1px solid #d4e1e9; border-radius: 8px; }
.coa-check input { width: 17px !important; min-height: 17px !important; margin: 0 !important; }
.amount-due {
  color: #d24048;
  font-weight: 800;
}
.context-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: -2px 0 12px;
  padding: 10px 12px;
  border: 1px solid #dce8ee;
  border-radius: 11px;
  background: #f8fbfc;
  color: #64778c;
  font-size: 11px;
}
.context-actions span i {
  margin-right: 7px;
  color: #15948c;
}
.context-actions .btn-outline {
  min-height: 34px;
  padding: 6px 11px;
}
.cash-account-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 12px;
}
.cash-account-grid article {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px;
  border: 1px solid #dbe6ed;
  border-radius: 12px;
  background: #fff;
}
.cash-account-grid article > span {
  display: grid;
  width: 39px;
  height: 39px;
  flex: 0 0 39px;
  place-items: center;
  border-radius: 10px;
}
.cash-account-grid article > span.bank {
  background: #e8f2fb;
  color: #17699a;
}
.cash-account-grid article > span.cash {
  background: #e7f7f2;
  color: #12836e;
}
.cash-account-grid small,
.cash-account-grid b,
.cash-account-grid strong {
  display: block;
}
.cash-account-grid small {
  color: #8293a4;
  font-size: 9px;
}
.cash-account-grid b {
  color: #354a60;
  font-size: 11px;
}
.cash-account-grid strong {
  margin-top: 3px;
  color: #163f61;
  font-size: 13px;
}
.subsection-title {
  margin: 18px 2px 9px;
}
.subsection-title small {
  color: #15948c;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
}
.subsection-title h3 {
  margin: 3px 0 0;
  color: #273a4e;
  font-size: 14px;
}
.accounting-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
}
.reconciliation-picker {
  border: 1px solid #dce7f1;
  border-radius: 14px;
  background: #f8fbfd;
  padding: 14px;
  max-height: 360px;
  overflow: auto;
}
.control-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin: 14px 0;
}
.control-grid article {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 15px 17px;
  border-radius: 14px;
  border: 1px solid #dce8ef;
  background: #fff;
}
.control-grid article > i {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  background: #e9f8f4;
  color: #078f82;
}
.control-grid article div {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.control-grid small {
  color: #68788d;
  font-weight: 700;
}
.control-grid b {
  font-size: 15px;
  color: #213047;
}
.control-grid span {
  font-size: 11px;
  color: #718096;
}
.control-grid article.warn {
  border-color: #f3c4c4;
  background: #fff8f8;
}
.control-grid article.warn > i {
  background: #fde9e9;
  color: #df4141;
}
.reconciliation-totals {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.reconciliation-totals span {
  background: #fff;
  border: 1px solid #e2eaf2;
  border-radius: 10px;
  padding: 9px 11px;
  font-size: 12px;
}
.reconciliation-totals b {
  display: block;
  margin-top: 3px;
  font-size: 13px;
}
.amount-ok {
  color: #07966f;
}
.accounting-panel {
  min-width: 0;
  padding: 14px;
}
.ledger-controls {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 10px;
}
.ledger-controls select {
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #d4e1e9;
  border-radius: 9px;
  background: #fff;
  color: #354b60;
}
.ledger-balance {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 9px;
  background: #edf7f6;
  color: #65798d;
  font-size: 10px;
}
.ledger-balance b,
.ledger-balance strong {
  margin-left: 5px;
  color: #163f61;
  font-size: 12px;
}
.row-actions {
  white-space: nowrap;
}
.row-actions button {
  width: 34px;
  height: 34px;
  margin-right: 5px;
  border: 0;
  border-radius: 8px;
  background: #e8f5f4;
  color: #087b77;
}
.row-actions button.danger {
  background: #fdecee;
  color: #c93e48;
}
.operation-form {
  display: grid;
  gap: 12px;
}
.operation-form label {
  margin: 0;
  color: #536a80;
  font-size: 11px;
  font-weight: 750;
}
.operation-form input,
.operation-form select,
.operation-form textarea {
  width: 100%;
  min-height: 42px;
  margin-top: 5px;
  padding: 9px 11px;
  border: 1px solid #d4e1e9;
  border-radius: 9px;
  background: #fff;
  color: #293b50;
}
.operation-form textarea {
  min-height: 72px;
}
.operation-summary {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px 14px;
  padding: 13px;
  border-radius: 10px;
  background: #f1f7f8;
}
.operation-summary span {
  color: #728599;
  font-size: 10px;
}
.operation-summary strong {
  color: #0d8882;
}
.warning-box {
  display: flex;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  background: #fff3df;
  color: #96610d;
  font-size: 11px;
}
.warning-box.info {
  background: #eaf5fb;
  color: #176a94;
}
.journal-detail-head {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  gap: 10px;
  padding: 13px;
  border-radius: 11px;
  background: #f4f8fa;
}
.journal-detail-head small,
.journal-detail-head b {
  display: block;
}
.journal-detail-head small {
  margin-bottom: 4px;
  color: #7a8da0;
  font-size: 9px;
}
.journal-description {
  margin: 13px 0;
  color: #455a70;
}
.status-badge {
  display: inline-block;
  padding: 5px 8px;
  border-radius: 99px;
  background: #eef3f7;
  color: #60748a;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
}
.status-badge.paid,
.status-badge.posted,
.status-badge.active,
.status-badge.reconciled,
.status-badge.approved {
  background: #e6f7ee;
  color: #168153;
}
.status-badge.partial,
.status-badge.open,
.status-badge.unmatched {
  background: #fff1dd;
  color: #a96800;
}
.aging-strip {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}
.aging-strip > div {
  padding: 12px;
  background: #fff;
  border: 1px solid #dae6ed;
  border-radius: 11px;
}
.aging-strip small,
.aging-strip b {
  display: block;
}
.aging-strip small {
  color: #718398;
  font-size: 9px;
}
.aging-strip b {
  margin-top: 4px;
  font-size: 12px;
}
.report-controls {
  display: grid;
  grid-template-columns: 1.3fr 1fr 1fr auto auto;
  gap: 8px;
  margin-bottom: 12px;
}
.report-paper {
  min-height: 400px;
}
.report-title {
  text-align: center;
  padding-bottom: 14px;
  border-bottom: 2px solid #214a69;
}
.report-title small {
  font-size: 9px;
  letter-spacing: 0.12em;
  color: #168e88;
}
.report-title h2 {
  margin: 4px 0;
  font-size: 19px;
}
.report-title p {
  margin: 0;
  color: #76869a;
  font-size: 10px;
}
.report-summary {
  width: 330px;
  margin: 16px 0 0 auto;
}
.report-empty {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  color: #8293a5;
}
.report-empty i {
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border-radius: 13px;
  background: #e8f5f4;
  color: #138b85;
  font-size: 18px;
}
.report-empty b {
  color: #40566d;
  font-size: 12px;
}
.report-empty span {
  font-size: 10px;
}
.report-summary > div {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
}
.report-summary > div:last-child {
  border-top: 2px solid #31485e;
  font-size: 14px;
}
@media (max-width: 1000px) {
  .accounting-columns {
    grid-template-columns: 1fr;
  }
  .metric-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .two-grid {
    grid-template-columns: 1fr;
  }
  .business-unit-grid {
    grid-template-columns: 1fr;
  }
  .journal-line {
    grid-template-columns: 1fr 1fr;
  }
  .report-controls {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 600px) {
  .cash-account-grid {
    grid-template-columns: 1fr;
  }
  .context-actions {
    align-items: stretch;
    flex-direction: column;
  }
  .entry-form,
  .budget-line {
    grid-template-columns: 1fr;
  }
  .entry-wide {
    grid-column: auto;
  }
  .metric-grid {
    grid-template-columns: 1fr;
  }
  .aging-strip {
    grid-template-columns: 1fr 1fr;
  }
  .form-grid,
  .journal-line,
  .report-controls {
    grid-template-columns: 1fr;
  }
  .form-actions {
    flex-wrap: wrap;
  }
  .report-summary {
    width: 100%;
  }
}
@media print {
  .section-head,
  .report-controls,
  :deep(.header),
  :deep(.aside) {
    display: none !important;
  }
  .finance-page {
    padding: 0;
  }
  .report-paper {
    border: 0;
    box-shadow: none;
  }
}
</style>
